// The character catalog: every character in a script, grouped by kind, and how many of each kind
// a game starts with. Shared by the Characters page, the in-game Characters sheet and How to play.
import type { SetupCount } from "../net/api";
import type { RoleCard, RoleType, Team } from "../protocol";
import { RoleIcon } from "./RoleIcon";

export const KINDS: {
  type: RoleType;
  team: Team;
  label: string;
  one: string;
  many: string;
  blurb: string;
}[] = [
  {
    type: "townsfolk",
    team: "good",
    label: "Townsfolk",
    one: "Townsfolk",
    many: "Townsfolk",
    blurb: "Good. Helpful abilities, mostly clues.",
  },
  {
    type: "outsider",
    team: "good",
    label: "Outsiders",
    one: "Outsider",
    many: "Outsiders",
    blurb: "Good, but their ability gets in the way.",
  },
  {
    type: "minion",
    team: "evil",
    label: "Minions",
    one: "Minion",
    many: "Minions",
    blurb: "Evil. They know the Demon and cause trouble.",
  },
  {
    type: "demon",
    team: "evil",
    label: "Demon",
    one: "Demon",
    many: "Demons",
    blurb: "Evil. Kills at night. Good must find them.",
  },
];

export function kindId(type: RoleType): string {
  return `kind-${type}`;
}

/** Chips that scroll to a part of the page. Buttons, not links: a room's URL must not change. */
export function JumpChips({ items }: { items: { id: string; label: string }[] }) {
  return (
    <nav className="toc" aria-label="Jump to">
      {items.map((it) => (
        <button
          key={it.id}
          type="button"
          className="chip"
          onClick={() =>
            document.getElementById(it.id)?.scrollIntoView({ behavior: "smooth", block: "start" })
          }
        >
          {it.label}
        </button>
      ))}
    </nav>
  );
}

/** The kinds this script has characters of, for the jump chips. */
export function kindJumps(roles: RoleCard[]): { id: string; label: string }[] {
  return KINDS.filter((k) => roles.some((r) => r.role_type === k.type)).map((k) => ({
    id: kindId(k.type),
    label: k.label,
  }));
}

export function CharacterList({ roles }: { roles: RoleCard[] }) {
  return (
    <>
      {KINDS.map((k) => {
        const ofKind = roles.filter((r) => r.role_type === k.type);
        if (ofKind.length === 0) return null;
        return (
          <div key={k.type} id={kindId(k.type)} className="card stack-sm kind-card">
            <div className="row-between">
              <h3>{k.label}</h3>
              <span className={`chip chip-${k.team}`}>{k.team === "good" ? "Good" : "Evil"}</span>
            </div>
            <ul className="role-list">
              {ofKind.map((r) => (
                <li key={r.id} className="role-row">
                  <RoleIcon roleId={r.id} width={48} className={r.team} />
                  <div>
                    <strong className={r.team}>{r.name}</strong>
                    <p className="small">{r.ability_text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
      <p className="tiny faint">* Not the first night.</p>
    </>
  );
}

const CHANGES = "Some characters change these numbers. Their card says how.";

/** One row per playable table size. */
export function CountTable({ counts }: { counts: SetupCount[] }) {
  return (
    <div className="stack-sm">
      <table className="count-table">
        <thead>
          <tr>
            <th scope="col">Players</th>
            {KINDS.map((k) => (
              <th key={k.type} scope="col" className={k.team}>
                {k.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {counts.map((c) => (
            <tr key={c.players}>
              <th scope="row">{c.players}</th>
              {KINDS.map((k) => (
                <td key={k.type}>{c[k.type]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="tiny faint">{CHANGES}</p>
    </div>
  );
}

/** The one row that matters at a table of this size, or nothing outside the playable range. */
export function CountSummary({ counts, players }: { counts: SetupCount[]; players: number }) {
  const row = counts.find((c) => c.players === players);
  if (!row) return null;
  return (
    <div className="stack-sm">
      <span className="eyebrow">With {players} players</span>
      <div className="row wrap" style={{ gap: 6 }}>
        {KINDS.map((k) => (
          <span key={k.type} className={`chip chip-${k.team}`}>
            {row[k.type]} {row[k.type] === 1 ? k.one : k.many}
          </span>
        ))}
      </div>
      <p className="tiny faint">{CHANGES}</p>
    </div>
  );
}
